import React, { useEffect, useState } from "react";
import RecordingRequestListing from "./RecordingRequestListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/RecordingRequestSlice";
import { useGetAllRecordingRequestQuery } from "src/services/RecordingRequestServices";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { FcApproval } from "react-icons/fc";
import { RxCrossCircled } from "react-icons/rx";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import StatusChangeDialogWrapper from "../StatusChangeDialog/StatusChangeDialogWrapper";
import DisApprovedStatusDialogWrapper from "../DisApprovedStatusDialog/DisApprovedStatusDialogWrapper";
import moment from "moment";

type Props = {};

const RecordingRequestListingWrapper = (props: Props) => {
  const dispatch = useDispatch();

  const [approveRequest, setApproveRequest] = useState(false);
  const [disApproveRequest, setdisApproveRequest] = useState(false);
  const [requestId, setRequestId] = useState();

  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    dateFilter,
    status,
  } = useSelector((state: RootState) => state.recordingRequest);

  const { data, isLoading, isFetching } = useGetAllRecordingRequestQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["requestedByName"],
    page: page,
    filterBy: [{ fieldName: "status", value: status }],
    dateFilter: {
      startDate: dateFilter?.start_date
        ? moment(dateFilter?.start_date).format("YYYY-MM-DD")
        : "",
      endDate: dateFilter?.end_date
        ? moment(dateFilter?.end_date).format("YYYY-MM-DD")
        : "",
    },
    orderBy: "status",
    orderByValue: -1,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
  }, [isLoading, isFetching, data, dispatch]);

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 font-semibold ">
            <FcApproval className="text-lg" /> Approve
          </div>
        ),
        onClick: () => {
          setApproveRequest(true);
          setRequestId(row);
        },
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <RxCrossCircled className="text-lg" /> Reject
          </div>
        ),
        onClick: () => {
          setdisApproveRequest(true);
          setRequestId(row);
        },
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date-Time",
      extraClasses: "min-w-[100px] py-1 max-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-base font-medium text-slate-700">
            {formatedDateTimeIntoIst(row?.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[12px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row?.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "recordingDate",
      headerName: "Requested For Date",
      extraClasses: "min-w-[100px] py-1 max-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          {row?.recordingDate && (
            <div className="text-base font-medium text-slate-700">
              {formatedDateTimeIntoIst(row?.recordingDate, "DD MMM yyyy")}
            </div>
          )}
          {row?.requestedForDate && (
            <div className="text-base font-medium text-slate-700">
              {formatedDateTimeIntoIst(row?.requestedForDate, "DD MMM yyyy")}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "requestedByName",
      headerName: "Student Name",
      extraClasses: "min-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <div>{row?.requestedByName}</div>,
    },
    {
      field: "batchName",
      headerName: "Batch",
      extraClasses: "min-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <div>{row?.batchName}</div>,
    },
    {
      field: "status",
      headerName: "Status",
      extraClasses: "min-w-[130px]",
      align: "center",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) =>
        row?.status === "APPROVED" ? (
          <div
            className="font-semibold
        rounded-full
        px-2
        py-[2px]
        text-[12px] text-green-700 bg-green-50 border border-green-50"
          >
            {row?.status}
          </div>
        ) : row?.status === "DISAPPROVED" ? (
          <div
            className="font-semibold
        rounded-full
        px-2
        py-[2px]
        text-[12px] text-red-700 bg-red-50 border border-red-50"
          >
            {row?.status}
          </div>
        ) : (
          <div
            className="font-semibold
        rounded-full
        px-2
        py-[2px]
        text-[12px] text-yellow-700 bg-yellow-100 border border-yellow-100"
          >
            {row?.status}
          </div>
        ),
    },
    {
      field: "approvedByName",
      headerName: "Action By",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <div>{row?.approvedByName}</div>,
    },
    {
      field: "approvedOnDate",
      headerName: "Action Date",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div className="text-xs">
          {" "}
          {row?.approvedOnDate &&
            formatedDateTimeIntoIst(row?.approvedOnDate, "DD MMM yyyy")}
        </div>
      ),
    },
    {
      field: "remark",
      headerName: "Student Reason",
      extraClasses:
        "py-2  overflow-scroll min-w-[200px] max-h-[100px]  max-w-[300px]",
      align: "center",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <div className="text-xs">{row?.remark}</div>,
    },
    {
      field: "approverRemark",
      headerName: "Reject Reason",
      extraClasses:
        "py-2  overflow-scroll min-w-[200px] max-h-[100px]  max-w-[300px]",
      align: "center",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div className="text-xs">{row?.approverRemark}</div>
      ),
    },
    {
      field: "requestedStartDate",
      headerName: "Want Access Date",
      extraClasses: "min-w-[130px]",
      align: "center",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          {row?.requestedStartDate && (
            <div className="text-xs font-medium text-slate-500">
              {formatedDateTimeIntoIst(row?.requestedStartDate, "DD MMM yyyy")}{" "}
              <span className="text-sm font-normal text-slate-700"> To </span>{" "}
              {formatedDateTimeIntoIst(row?.requestedEndDate, "DD MMM yyyy")}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "accessStartDate",
      headerName: "Access Date",
      extraClasses: "min-w-[130px]",
      align: "center",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          {row?.accessStartDate && (
            <div className="text-xs font-medium text-slate-700">
              {formatedDateTimeIntoIst(row?.accessStartDate, "DD MMM yyyy")}{" "}
              <span className="text-sm font-medium text-slate-900"> To </span>{" "}
              {formatedDateTimeIntoIst(row?.accessEndDate, "DD MMM yyyy")}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <RecordingRequestListing
        columns={columns}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {approveRequest && (
        <StatusChangeDialogWrapper
          requestId={requestId}
          onClose={() => setApproveRequest(false)}
        />
      )}
      {disApproveRequest && (
        <DisApprovedStatusDialogWrapper
          requestId={requestId}
          onClose={() => setdisApproveRequest(false)}
        />
      )}
    </div>
  );
};

export default RecordingRequestListingWrapper;
